import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Input,
  Row,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';
import * as api from '@apis/grouping-agent';
import {
  GroupingAgentGroup,
  GroupingAgentRunItem,
  OptionMappingReview,
  GroupingMode,
  RefundRiskBand,
  TriggerBand,
} from '@models/grouping-agent';
import { formatGroupingRunDuration } from '@utils/grouping-agent';
import {
  ROUTING_MODE_COLOR,
  RUN_STATUS_COLOR,
  groupStatusLabel,
  invocationTypeLabel,
  reservationGradeLabel,
  routingModeLabel,
  runStatusLabel,
} from '@utils/reservation-success-labels';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';
import KakaoOptionMappingTab from '@components/grouping-agent/KakaoOptionMappingTab';
import RunItemDetailModal from '@components/grouping-agent/RunItemDetailModal';
import GroupEnrollmentsModal from '@components/grouping-agent/GroupEnrollmentsModal';

const { Title } = Typography;

const riskColor: Record<RefundRiskBand, string> = {
  stable: 'green',
  caution: 'orange',
  danger: 'red',
};

type RunItemSummary = NonNullable<GroupingAgentRunItem['itemSummary']>;

const sortByItemSummary = (key: keyof RunItemSummary) => (a: GroupingAgentRunItem, b: GroupingAgentRunItem) =>
  (a.itemSummary?.[key] ?? 0) - (b.itemSummary?.[key] ?? 0);

const matchGroupSearch = (group: GroupingAgentGroup, keyword: string) => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  const haystack = [
    group.partnerName,
    group.productName,
    group.optionName,
    group.name,
    group.groupStatus,
    group.refundRiskBand,
    group.triggerBand,
    group.startDate ? dayjs(group.startDate).format('YYYY-MM-DD HH:mm') : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(kw);
};

const GroupingAgentRunDetailPage: React.FC = () => {
  const { runId = '' } = useParams();
  const [groupFilter, setGroupFilter] = useState<'all' | 'complete' | 'short'>('all');
  const [groupSearch, setGroupSearch] = useState('');
  const [kakaoOnlyFilter, setKakaoOnlyFilter] = useState(false);
  const [detailRunItem, setDetailRunItem] = useState<GroupingAgentRunItem | null>(null);
  const [enrollmentGroup, setEnrollmentGroup] = useState<GroupingAgentGroup | null>(null);

  const runQuery = useQuery({
    queryKey: ['grouping-agent-run', runId],
    queryFn: () => api.getGroupingAgentRun(runId),
    enabled: !!runId,
    refetchInterval: (q) => (q.state.data?.data?.status === 'running' ? 3000 : false),
  });

  const itemsQuery = useQuery({
    queryKey: ['grouping-agent-run-items', runId],
    queryFn: () => api.getGroupingAgentRunItems(runId),
    enabled: !!runId,
  });

  const groupsQuery = useQuery({
    queryKey: ['grouping-agent-run-groups', runId, groupFilter],
    queryFn: () =>
      api.getGroupingAgentRunGroups(runId, groupFilter === 'all' ? undefined : { groupStatus: groupFilter }),
    enabled: !!runId,
  });

  const shortGroupsQuery = useQuery({
    queryKey: ['grouping-agent-run-groups-short', runId],
    queryFn: () => api.getGroupingAgentRunGroups(runId, { groupStatus: 'short' }),
    enabled: !!runId,
  });

  const mappingQuery = useQuery({
    queryKey: ['grouping-agent-option-mapping', runId],
    queryFn: () => api.getGroupingAgentOptionMappingReviews(runId),
    enabled: !!runId,
  });

  const run = runQuery.data?.data;
  const summary = run?.summary;
  const isRunning = run?.status === 'running';

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const durationText = run
    ? formatGroupingRunDuration(run.triggeredAt, run.completedAt, isRunning ? now : undefined)
    : '-';

  const matchKakaoFilter = (group: GroupingAgentGroup) => !kakaoOnlyFilter || !!group.hasKakaoEnrollment;

  const shortGroupsAll = useMemo(() => {
    const list = shortGroupsQuery.data?.data || [];
    return [...list].sort((a, b) => (b.minNeeded ?? 0) - (a.minNeeded ?? 0));
  }, [shortGroupsQuery.data?.data]);

  const shortGroups = useMemo(() => shortGroupsAll.filter(matchKakaoFilter), [shortGroupsAll, kakaoOnlyFilter]);

  const groupsAll = useMemo(() => groupsQuery.data?.data || [], [groupsQuery.data?.data]);

  const filteredGroups = useMemo(() => {
    return groupsAll.filter((g) => matchGroupSearch(g, groupSearch) && matchKakaoFilter(g));
  }, [groupsAll, groupSearch, kakaoOnlyFilter]);

  const allGroupsCount = groupsAll.length;
  const shortGroupsAllCount = shortGroupsAll.length;

  const kakaoFilterControl = (
    <Checkbox checked={kakaoOnlyFilter} onChange={(e) => setKakaoOnlyFilter(e.target.checked)}>
      카카오톡 예약하기 포함
    </Checkbox>
  );

  const kakaoColumn: ColumnsType<GroupingAgentGroup>[0] = {
    title: '카카오',
    width: 72,
    align: 'center',
    sorter: (a, b) => (a.kakaoEnrollmentCount ?? 0) - (b.kakaoEnrollmentCount ?? 0),
    render: (_, r) => ((r.kakaoEnrollmentCount ?? 0) > 0 ? <Tag color="yellow">{r.kakaoEnrollmentCount}</Tag> : '-'),
  };

  const partnerNameColumn = <T extends { partnerName?: string }>(): ColumnsType<T>[0] => ({
    title: '파트너명',
    dataIndex: 'partnerName',
    ellipsis: true,
    width: 120,
    render: (v?: string) => v || '-',
    sorter: (a, b) => (a.partnerName || '').localeCompare(b.partnerName || ''),
  });

  const productNameColumn = <T extends { productName?: string }>(): ColumnsType<T>[0] => ({
    title: '상품명',
    dataIndex: 'productName',
    ellipsis: true,
    width: 200,
    render: (v?: string) => v || '-',
    sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
  });

  const shortColumns: ColumnsType<GroupingAgentGroup> = [
    partnerNameColumn<GroupingAgentGroup>(),
    productNameColumn<GroupingAgentGroup>(),
    kakaoColumn,
    { title: '회차명', dataIndex: 'optionName', ellipsis: true, width: 180, render: (v?: string) => v || '-' },
    {
      title: '수업일',
      dataIndex: 'startDate',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
      sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
    },
    { title: '그룹명', dataIndex: 'name', width: 100, sorter: (a, b) => (a.name || '').localeCompare(b.name || '') },
    {
      title: '현재/최소/최대',
      width: 110,
      render: (_, r) => `${r.currentCount}/${r.studentsMin}/${r.studentsMax < 0 ? '제한없음' : r.studentsMax}`,
      sorter: (a, b) => a.currentCount - b.currentCount,
    },
    {
      title: '보충 필요',
      dataIndex: 'minNeeded',
      width: 100,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.minNeeded - b.minNeeded,
      render: (n: number) => <Badge count={n} showZero color={n > 0 ? 'red' : 'default'} />,
    },
    {
      title: '추가 모집',
      dataIndex: 'spotsRemaining',
      width: 90,
      sorter: (a, b) => a.spotsRemaining - b.spotsRemaining,
      render: (n: number, r) => (r.studentsMax < 0 && r.groupStatus === 'complete' ? '-' : n),
    },
    {
      title: '표시',
      width: 200,
      render: (_, r) => (
        <Space size={4} wrap>
          {(r.namePendingCount ?? 0) > 0 && <Tag color="orange">이름 보충 {r.namePendingCount}명</Tag>}
          {r.needsRecruitment && <Tag color="volcano">라스트찬스 +{r.minNeeded}명</Tag>}
        </Space>
      ),
    },
    {
      title: '상세',
      width: 80,
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => setEnrollmentGroup(r)}>
          수강생
        </Button>
      ),
    },
    {
      title: '환불위험',
      dataIndex: 'refundRiskBand',
      render: (b?: RefundRiskBand) => (b ? <Tag color={riskColor[b]}>{reservationGradeLabel(b)}</Tag> : '-'),
    },
    {
      title: '트리거',
      dataIndex: 'triggerBand',
      render: (b?: TriggerBand) =>
        b ? <Tag color={b === 'danger' ? 'red' : 'orange'}>{reservationGradeLabel(b)}</Tag> : '-',
    },
  ];

  const itemColumns: ColumnsType<GroupingAgentRunItem> = [
    partnerNameColumn<GroupingAgentRunItem>(),
    {
      title: '상품',
      dataIndex: 'productName',
      ellipsis: true,
      width: 200,
      render: (v?: string) => v || '-',
      sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
    },
    {
      title: '회차명',
      dataIndex: 'optionName',
      ellipsis: true,
      width: 180,
      render: (v?: string) => v || '-',
      sorter: (a, b) => (a.optionName || '').localeCompare(b.optionName || ''),
    },
    {
      title: '',
      key: 'detail',
      width: 88,
      render: (_, r) => {
        const unassigned = r.itemSummary?.unassignedCount ?? 0;
        return (
          <Button type="link" size="small" onClick={() => setDetailRunItem(r)}>
            상세{unassigned > 0 ? ` (${unassigned})` : ''}
          </Button>
        );
      },
    },
    {
      title: '수업일',
      dataIndex: 'startDate',
      width: 150,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
    },
    {
      title: '모드',
      dataIndex: 'groupingMode',
      render: (m?: GroupingMode) => (m ? <Tag color={ROUTING_MODE_COLOR[m]}>{routingModeLabel(m)}</Tag> : '-'),
    },
    {
      title: '트리거',
      dataIndex: 'triggerBand',
      render: (b: TriggerBand) => <Tag color={b === 'danger' ? 'red' : 'orange'}>{reservationGradeLabel(b)}</Tag>,
    },
    {
      title: '환불위험',
      dataIndex: 'refundRiskBand',
      render: (b?: RefundRiskBand) => (b ? <Tag color={riskColor[b]}>{reservationGradeLabel(b)}</Tag> : '-'),
    },
    { title: '결제건', dataIndex: 'enrollmentCount', sorter: (a, b) => a.enrollmentCount - b.enrollmentCount },
    {
      title: <Tooltip title="이 회차에 생성된 그룹 수">그룹</Tooltip>,
      width: 72,
      align: 'center',
      sorter: sortByItemSummary('groupCount'),
      render: (_, r) => (r.itemSummary ? r.itemSummary.groupCount : '-'),
    },
    {
      title: <Tooltip title="최소 인원을 충족한 그룹 수">완료</Tooltip>,
      width: 72,
      align: 'center',
      sorter: sortByItemSummary('completeGroupCount'),
      render: (_, r) => (r.itemSummary ? r.itemSummary.completeGroupCount : '-'),
    },
    {
      title: <Tooltip title="최소 인원이 부족한 그룹 수 (보충 필요)">미달</Tooltip>,
      width: 72,
      align: 'center',
      sorter: sortByItemSummary('shortGroupCount'),
      render: (_, r) => {
        if (!r.itemSummary) return '-';
        const n = r.itemSummary.shortGroupCount;
        return n > 0 ? <Badge count={n} showZero color="red" /> : 0;
      },
    },
    {
      title: <Tooltip title="어떤 그룹에도 배정되지 않은 예약 건수">미배정</Tooltip>,
      width: 80,
      align: 'center',
      sorter: sortByItemSummary('unassignedCount'),
      render: (_, r) => {
        if (!r.itemSummary) return '-';
        const n = r.itemSummary.unassignedCount;
        return n > 0 ? <Badge count={n} color="red" /> : 0;
      },
    },
    {
      title: '카카오 매핑',
      render: (_, r) => {
        const m = r.optionMappingSummary;
        if (!m) return '-';
        return `resolved ${m.resolved ?? 0} / pending ${m.pendingReview ?? 0}`;
      },
    },
    { title: '상태', dataIndex: 'status', render: (s: string) => runStatusLabel(s) },
  ];

  const groupColumns: ColumnsType<GroupingAgentGroup> = [
    partnerNameColumn<GroupingAgentGroup>(),
    productNameColumn<GroupingAgentGroup>(),
    kakaoColumn,
    {
      title: '회차명',
      dataIndex: 'optionName',
      ellipsis: true,
      width: 180,
      render: (v?: string) => v || '-',
      sorter: (a, b) => (a.optionName || '').localeCompare(b.optionName || ''),
    },
    {
      title: '수업일',
      dataIndex: 'startDate',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
      sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
    },
    { title: '그룹명', dataIndex: 'name', width: 100, sorter: (a, b) => (a.name || '').localeCompare(b.name || '') },
    {
      title: '현재/최소/최대',
      width: 110,
      render: (_, r) => `${r.currentCount}/${r.studentsMin}/${r.studentsMax < 0 ? '제한없음' : r.studentsMax}`,
      sorter: (a, b) => a.currentCount - b.currentCount,
    },
    {
      title: '상태',
      dataIndex: 'groupStatus',
      width: 100,
      sorter: (a, b) => (a.groupStatus || '').localeCompare(b.groupStatus || ''),
      render: (s: string) => <Tag color={s === 'short' ? 'red' : 'green'}>{groupStatusLabel(s)}</Tag>,
    },
    {
      title: '표시',
      width: 200,
      render: (_, r) => (
        <Space size={4} wrap>
          {(r.namePendingCount ?? 0) > 0 && <Tag color="orange">이름 보충 {r.namePendingCount}명</Tag>}
          {r.needsRecruitment && <Tag color="volcano">라스트찬스 +{r.minNeeded}명</Tag>}
        </Space>
      ),
    },
    {
      title: '보충 필요',
      dataIndex: 'minNeeded',
      width: 100,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.minNeeded - b.minNeeded,
      render: (n: number) => <Badge count={n} showZero color={n > 0 ? 'red' : 'default'} />,
    },
    {
      title: '추가 모집',
      dataIndex: 'spotsRemaining',
      width: 90,
      sorter: (a, b) => a.spotsRemaining - b.spotsRemaining,
      render: (n: number, r) => (r.studentsMax < 0 && r.groupStatus === 'complete' ? '-' : n),
    },
    {
      title: '상세',
      width: 80,
      fixed: 'right',
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => setEnrollmentGroup(r)}>
          수강생
        </Button>
      ),
    },
    {
      title: '환불위험',
      dataIndex: 'refundRiskBand',
      width: 90,
      sorter: (a, b) => (a.refundRiskBand || '').localeCompare(b.refundRiskBand || ''),
      render: (b?: RefundRiskBand) => (b ? <Tag color={riskColor[b]}>{reservationGradeLabel(b)}</Tag> : '-'),
    },
    {
      title: '트리거',
      dataIndex: 'triggerBand',
      width: 90,
      sorter: (a, b) => (a.triggerBand || '').localeCompare(b.triggerBand || ''),
      render: (b?: TriggerBand) =>
        b ? <Tag color={b === 'danger' ? 'red' : 'orange'}>{reservationGradeLabel(b)}</Tag> : '-',
    },
  ];

  const mappingReviews = (mappingQuery.data?.data as OptionMappingReview[]) || [];

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title={`그룹핑 실행 ${runId.slice(0, 8)}`}
        parent={{ label: '그룹핑 실행', to: '/grouping-agent-runs' }}
        description="이 실행에서 만든 그룹과 회차입니다. 성사 등급 진단은 배치 실행에서 확인합니다."
        extra={
          <Space>
            <Link to="/grouping-agent-runs">
              <Button>← 목록</Button>
            </Link>
            <Button onClick={() => runQuery.refetch()} loading={runQuery.isFetching}>
              새로고침
            </Button>
          </Space>
        }
      />
      <Space style={{ marginBottom: 16 }}>
        <Tag color={RUN_STATUS_COLOR[run?.status || ''] || 'processing'}>
          {run?.status ? runStatusLabel(run.status) : '불러오는 중'}
        </Tag>
        {run?.invocationType && <Tag>{invocationTypeLabel(run.invocationType)}</Tag>}
        {run?.triggerDate && <span>기준일 {dayjs(run.triggerDate).format('YYYY-MM-DD')}</span>}
        {run?.routingSummary && (
          <span>
            전체 재배정 {run.routingSummary.full} · 증분 {run.routingSummary.incremental} · 생략{' '}
            {run.routingSummary.skipped}
          </span>
        )}
        {run?.error && <Tag color="error">{run.error}</Tag>}
      </Space>

      {run && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="시작" value={dayjs(run.triggeredAt).format('MM-DD HH:mm:ss')} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic
              title="완료"
              value={run.completedAt ? dayjs(run.completedAt).format('MM-DD HH:mm:ss') : isRunning ? '—' : '-'}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic
              title="소요 시간"
              value={durationText}
              valueStyle={isRunning ? { color: '#1677ff' } : undefined}
              suffix={
                isRunning ? (
                  <Tag color="processing" style={{ marginLeft: 8 }}>
                    진행 중
                  </Tag>
                ) : undefined
              }
            />
          </Col>
        </Row>
      )}

      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="상품" value={summary.productCount} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="회차(옵션)" value={summary.optionCount} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="결제건" value={summary.enrollmentCount} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="생성 그룹" value={summary.groupCount} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="안정 회차" value={summary.stableScheduleCount} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="보충 필요 그룹" value={summary.shortGroupCount} valueStyle={{ color: '#cf1322' }} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Statistic title="미배정" value={summary.unassignedCount} />
          </Col>
        </Row>
      )}

      <Space style={{ marginBottom: 8 }} align="center">
        <Title level={5} style={{ margin: 0 }}>
          보충 필요 그룹
        </Title>
        {kakaoFilterControl}
        <Typography.Text type="secondary">
          {kakaoOnlyFilter && shortGroups.length !== shortGroupsAllCount
            ? `${shortGroups.length}건 / 전체 ${shortGroupsAllCount}건`
            : `총 ${shortGroupsAllCount}건`}
        </Typography.Text>
      </Space>
      <Table
        size="small"
        rowKey="_id"
        loading={shortGroupsQuery.isLoading}
        columns={shortColumns}
        dataSource={shortGroups}
        pagination={{ pageSize: 10 }}
        style={{ marginBottom: 24 }}
      />

      <Tabs
        items={[
          {
            key: 'items',
            label: '회차',
            children: (
              <Table
                rowKey="_id"
                loading={itemsQuery.isLoading}
                columns={itemColumns}
                dataSource={itemsQuery.data?.data || []}
                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
                scroll={{ x: 1400 }}
              />
            ),
          },
          {
            key: 'groups',
            label: '모든 그룹',
            children: (
              <>
                <Space wrap style={{ marginBottom: 12 }} size="middle">
                  <Input.Search
                    allowClear
                    placeholder="파트너명, 상품명, 회차명, 그룹명, 상태 검색"
                    style={{ width: 280 }}
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                  />
                  <Button type={groupFilter === 'all' ? 'primary' : 'default'} onClick={() => setGroupFilter('all')}>
                    전체
                  </Button>
                  <Button
                    type={groupFilter === 'complete' ? 'primary' : 'default'}
                    onClick={() => setGroupFilter('complete')}>
                    완료
                  </Button>
                  <Button
                    type={groupFilter === 'short' ? 'primary' : 'default'}
                    onClick={() => setGroupFilter('short')}>
                    보충 필요
                  </Button>
                  {kakaoFilterControl}
                  <Typography.Text type="secondary">
                    {filteredGroups.length === allGroupsCount
                      ? `총 ${allGroupsCount}건`
                      : `${filteredGroups.length}건 / 전체 ${allGroupsCount}건`}
                  </Typography.Text>
                </Space>
                <Table
                  rowKey="_id"
                  loading={groupsQuery.isLoading}
                  columns={groupColumns}
                  dataSource={filteredGroups}
                  pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
                  scroll={{ x: 1420 }}
                />
              </>
            ),
          },
          {
            key: 'mapping',
            label: `카카오 매핑${
              mappingReviews.some((r) => r.status === 'pending')
                ? ` (${mappingReviews.filter((r) => r.status === 'pending').length})`
                : ''
            }`,
            children: <KakaoOptionMappingTab runId={runId} reviews={mappingReviews} loading={mappingQuery.isLoading} />,
          },
        ]}
      />

      <RunItemDetailModal
        runId={runId}
        runItem={detailRunItem}
        open={!!detailRunItem}
        onClose={() => setDetailRunItem(null)}
      />

      <GroupEnrollmentsModal
        runId={runId}
        group={enrollmentGroup}
        open={!!enrollmentGroup}
        onClose={() => setEnrollmentGroup(null)}
      />
    </div>
  );
};

export default GroupingAgentRunDetailPage;
